/**
 * Frontend view models mirroring the Core Platform API contract.
 * (Kept in the app rather than importing server types, which are not part of
 * the shared package surface.)
 */
export type SubscriptionPlanId = 'free' | 'starter' | 'growth' | 'professional' | 'enterprise';

export interface PlanLimits {
  maxCampaigns: number;
  maxActiveCampaigns: number;
  monthlyGamePlays: number;
}

export interface PlanView {
  id: SubscriptionPlanId;
  name: string;
  price: number;
  currency: string;
  interval: string;
  trialDays: number;
  features: string[];
  limits: PlanLimits;
}

export interface CurrentBillingView {
  plan: PlanView;
  features: string[];
  limits: PlanLimits;
  subscriptionStatus: string;
  /** True when Shopify Managed Pricing owns plan selection (hosted by Shopify). */
  managedPricing?: boolean;
  /** Shopify's hosted pricing page URL, when Managed Pricing is active. */
  managedPricingUrl?: string | null;
}

export interface FeaturesView {
  plan: SubscriptionPlanId;
  features: string[];
  limits: PlanLimits;
}

export interface AnalyticsOverview {
  gameStarts: number;
  gameCompletes: number;
  rewardsWon: number;
  couponsCreated: number;
  couponsUsed: number;
  completionRate: number;
  redemptionRate: number;
}

export interface AnalyticsEvent {
  id: string;
  eventType: string;
  campaignId: string | null;
  customerId: string | null;
  createdAt: string;
}

export type CouponStatusId = 'active' | 'redeemed' | 'expired' | 'void';
export type CouponSyncStatus = 'synced' | 'pending' | 'failed';

export interface CouponView {
  id: string;
  campaignId: string;
  code: string;
  discountType: 'percentage' | 'fixed' | 'free_shipping';
  discountValue: number;
  minimumCart: number | null;
  expiresAt: string | null;
  status: CouponStatusId;
  singleUse: boolean;
  usageLimit: number | null;
  usedCount: number;
  /** Shopify mirroring state; null when discount sync isn't applicable. */
  syncStatus: CouponSyncStatus | null;
  createdAt: string;
  updatedAt: string;
}

/** Live check of Shopify's actually-granted scopes vs. what discount sync needs. */
export interface CouponSyncHealth {
  granted: string[];
  missing: string[];
  /** True when the stored access token is invalid/revoked, not just under-scoped. */
  tokenInvalid?: boolean;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomerCoupon {
  code: string;
  status: string;
  discountType: string;
  discountValue: number;
  createdAt: string;
}

export interface CustomerRedemption {
  orderId: string;
  amount: number | null;
  redeemedAt: string;
}

export interface CustomerSummary {
  id: string;
  kind: 'customer' | 'guest';
  label: string;
  customerId: string | null;
  plays: number;
  rewardsWon: number;
  couponsRedeemed: number;
  totalSpent: number;
  lastActiveAt: string;
  coupons: CustomerCoupon[];
  redemptions: CustomerRedemption[];
}

export interface CustomerStats {
  totalPlayers: number;
  customers: number;
  guests: number;
  rewardsWon: number;
  redemptions: number;
  revenue: number;
  conversionRate: number;
}

export interface CustomersResult {
  stats: CustomerStats;
  items: CustomerSummary[];
  /** True when older activity was omitted because a data source hit its cap. */
  truncated?: boolean;
}

export type PopupPosition = 'center' | 'bottom-right' | 'bottom-left';
export type PopupSize = 'small' | 'medium' | 'large';

export interface ThemeConfig {
  colors: {
    primary: string;
    background: string;
    text: string;
    button: string;
    buttonText: string;
    /** Spin wheel frame. Falls back to `primary` when unset. */
    wheelBezel?: string;
    /** Spin wheel metallic accent (studs, hub, pointer). Falls back to gold. */
    wheelAccent?: string;
  };
  fontFamily: string;
  popup: { position: PopupPosition; size: PopupSize };
  borderRadius: number;
  confetti: boolean;
}

export interface ThemePreset {
  key: string;
  name: string;
  config: ThemeConfig;
}

export interface ThemeView {
  id: string;
  campaignId: string;
  config: ThemeConfig;
}

export interface CampaignListItem {
  id: string;
  campaignName: string;
  status: string;
}

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'published'
  | 'paused'
  | 'expired'
  | 'archived';

export interface CampaignView {
  id: string;
  campaignName: string;
  description: string | null;
  status: CampaignStatus;
  gameId: string | null;
  rewardId: string | null;
  ruleId: string | null;
  themeId: string | null;
  publishAt: string | null;
  expireAt: string | null;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export type GameTypeId =
  | 'spin_wheel'
  | 'scratch_card'
  | 'lucky_box'
  | 'gift_box'
  | 'mystery_gift';

export interface GameTypeOption {
  type: GameTypeId;
  name: string;
}

export interface GameView {
  id: string;
  type: GameTypeId;
  name: string | null;
  config: Record<string, unknown>;
  status: string;
}

export type RewardTypeId =
  | 'percentage'
  | 'fixed'
  | 'free_shipping'
  | 'free_product'
  | 'store_credit'
  | 'points'
  | 'badge'
  | 'mystery'
  | 'no_reward'
  | 'try_again';

export interface RewardView {
  id: string;
  campaignId: string;
  type: RewardTypeId;
  label: string | null;
  value: number | null;
  weight: number;
  inventory: number | null;
  remainingClaims: number | null;
  productId: string | null;
  productTitle: string | null;
  productImage: string | null;
  expiryDays: number | null;
  status: string;
}

export const REWARD_TYPE_OPTIONS: Array<{ label: string; value: RewardTypeId }> = [
  { label: 'Percentage off', value: 'percentage' },
  { label: 'Fixed amount off', value: 'fixed' },
  { label: 'Free shipping', value: 'free_shipping' },
  { label: 'Free product', value: 'free_product' },
  { label: 'Store credit', value: 'store_credit' },
  { label: 'No win', value: 'no_reward' },
  { label: 'Try again', value: 'try_again' },
];

/** Reward types whose `value` field is meaningful. */
export const VALUE_REWARD_TYPES: RewardTypeId[] = [
  'percentage',
  'fixed',
  'store_credit',
  'points',
];

export type CustomerTypeId = 'any' | 'guest' | 'logged_in';
export type AttemptFrequencyId = 'one_time' | 'daily' | 'weekly' | 'monthly' | 'unlimited';

export interface RulesConfig {
  displayRules?: { pages?: string[]; productIds?: string[]; collectionIds?: string[]; customUrls?: string[] };
  audienceRules?: { customerType?: CustomerTypeId; countries?: string[]; devices?: string[] };
  attemptRules?: { frequency: AttemptFrequencyId; maxAttempts?: number };
}

export interface RulesView extends RulesConfig {
  id: string;
  campaignId: string;
  priority: number;
  status: string;
}

/** Storefront defaults for the wheel board when a theme leaves them unset. */
export const WHEEL_ACCENT_FALLBACK = '#c8951a';

export const DEFAULT_THEME: ThemeConfig = {
  colors: {
    primary: '#6366f1',
    background: '#ffffff',
    text: '#111827',
    button: '#111827',
    buttonText: '#ffffff',
  },
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  popup: { position: 'center', size: 'medium' },
  borderRadius: 16,
  confetti: true,
};

export interface ChangePlanResult {
  plan: SubscriptionPlanId;
  confirmationUrl: string | null;
  status: string;
}

export interface MerchantProfile {
  id: string;
  shopDomain: string;
  storeName: string;
  email: string | null;
  country: string | null;
  currency: string | null;
  timezone: string | null;
  language: string | null;
  subscriptionPlan: SubscriptionPlanId;
  status: string;
  logo: string | null;
  brandColor: string | null;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Editable subset of the merchant profile (PUT /merchant/profile). */
export interface UpdateMerchantProfile {
  storeName?: string;
  email?: string;
  country?: string;
  currency?: string;
  timezone?: string;
  language?: string;
  logo?: string;
  brandColor?: string;
}

/** Human labels for feature flags returned by the API. */
export const FEATURE_LABELS: Record<string, string> = {
  email_capture: 'Email capture',
  scheduling: 'Campaign scheduling',
  multiple_games: 'Multiple games',
  advanced_analytics: 'Advanced analytics',
  custom_branding: 'Custom branding',
  api_access: 'API access',
  priority_support: 'Priority support',
  white_label: 'White label',
};

export function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key;
}

export function formatLimit(value: number): string {
  return value === -1 ? 'Unlimited' : value.toLocaleString();
}

/** Format a 0–1 rate as a whole percentage, guarding against missing/NaN values. */
export function formatPercent(rate: number | null | undefined): string {
  const n = typeof rate === 'number' && Number.isFinite(rate) ? rate : 0;
  return `${Math.round(n * 100)}%`;
}

/** Format a money amount in the merchant's currency (falls back to USD). */
export function formatMoney(amount: number, currency?: string | null): string {
  const code = currency && currency.length === 3 ? currency.toUpperCase() : 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}
